import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { product_category } from '@prisma/client';
import {
    ColumnDef,
    ColumnFiltersState,
    PaginationState,
    SortingState,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { format } from 'date-fns';
import { ArrowDown, ArrowUp, ArrowUpDown, Loader2, MoreHorizontal, Plus } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { trpc } from '~/api/trpc';
import Content from '../components/layout/Content';
import { Skeleton } from '../components/ui/skeleton';
import { useShallow } from 'zustand/react/shallow';
import { useTreatmentsTableFilteringStore } from '~/stores/treatments-table-filtering-store';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '~/components/ui/tabs';
import TreatmentsOrdering from '~/pages/TreatmentsOrder';

const CreateTreatmentButton = () => {
    const navigate = useNavigate();
    const createTreatmentMutation = trpc.treatments.create.useMutation();

    const handleSubmit = async () => {
        const defaultName = "New Treatment";
        const newTreatment = await createTreatmentMutation.mutateAsync({ name: defaultName });

        if (newTreatment) {
            toast.success("Created new treatment");
            navigate(`/treatment/${newTreatment.id}`);
        } else {
            toast.error("Failed to create treatment", { description: newTreatment });
        }
    }

    return (
        <Button
            variant='outline'
            className='border-primary-400 text-primary-400 hover:text-primary-400 hover:bg-primary-400/10 transition-all'
            disabled={createTreatmentMutation.isPending}
            onClick={handleSubmit}
        >
            {createTreatmentMutation.isPending ? (
                <Loader2 className='mr-1 h-4 w-4 animate-spin' />
            ) : (
                <Plus className='mr-1 h-4 w-4' />
            )}
            New Treatment
        </Button>
    )
}

// Define the Treatment type based on the NewTreatment model
export type Treatment = {
    id: string;
    idx: number;
    name: string;
    shortDescription: string;
    longDescription: string;
    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
    categoryId: number | null;
    category: product_category | null;
};

export const columns: ColumnDef<Treatment>[] = [
    {
        accessorKey: 'idx',
        header: 'ID',
    },
    {
        accessorKey: 'name',
        header: ({ column }) => {
            return (
                <Button
                    variant='ghost'
                    onClick={() => {
                        const currentSort = column.getIsSorted();
                        if (currentSort === 'asc') {
                            column.toggleSorting(true); // Toggle to desc
                        } else if (currentSort === 'desc') {
                            column.clearSorting(); // Clear sorting (to none)
                        } else {
                            column.toggleSorting(false); // Toggle to asc
                        }
                    }}
                >
                    Name
                    {column.getIsSorted() === 'asc' ? (
                        <ArrowUp className='ml-2 h-4 w-4' />
                    ) : column.getIsSorted() === 'desc' ? (
                        <ArrowDown className='ml-2 h-4 w-4' />
                    ) : (
                        <ArrowUpDown className='ml-2 h-4 w-4 opacity-50' />
                    )}
                </Button>
            );
        },
    },
    {
        accessorKey: 'category',
        header: 'Category',
        cell: ({ row }) => {
            return <div className='max-w-[300px] truncate'>{row.original.category?.name}</div>;
        },
    },
    {
        id: 'createdAt',
        header: 'Created At',
        cell: ({ row }) => {
            const date = new Date(row.original.createdAt);
            return <div>{format(date, 'PPP')}</div>;
        },
        accessorFn: (row) => new Date(row.createdAt).getTime(),
    },
    {
        id: 'actions',
        cell: ({ row }) => {
            const treatment = row.original;

            return (
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant='ghost' className='h-8 w-8 p-0'>
                            <span className='sr-only'>Open menu</span>
                            <MoreHorizontal className='h-4 w-4' />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align='end'>
                        <DropdownMenuLabel>Actions</DropdownMenuLabel>
                        <DropdownMenuItem onClick={() => navigator.clipboard.writeText(treatment.id)}>Copy treatment ID</DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem>View treatment details</DropdownMenuItem>
                        <DropdownMenuItem>Edit treatment</DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            );
        },
    },
];

interface DataTableProps<TData, TValue> {
    columns: ColumnDef<TData, TValue>[];
    // Assuming TData has an 'id' property
    manualPagination?: boolean;
    manualSorting?: boolean;
    manualFiltering?: boolean;
    syncWithUrl?: boolean;
    // Add other props like pageCount, isLoading etc. if fetching happens outside
}

// Define a more specific type for your data if possible
interface TreatmentData {
    id: string;
    name: string;
    // Add other fields relevant to your columns
}

// Example Usage Type (Adjust as needed)
type TreatmentTableProps = DataTableProps<TreatmentData, unknown>;


export const DataTable = <TData, TValue>({
    columns,
    manualPagination = true, // Defaulting to true as you fetch server-side
    manualSorting = true,
    manualFiltering = true,
    syncWithUrl = true, // Defaulting to true based on your goal
}: DataTableProps<TData, TValue>) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const isInitialized = useRef<boolean>(false); // To prevent double-running in StrictMode/future React versions

    // 1. Get state and setters from Zustand store
    const {
        sorting: storeSorting,
        columnFilters: storeColumnFilters,
        pagination: storePagination,
        setSorting,
        setColumnFilters,
        setPagination,
    } = useTreatmentsTableFilteringStore(
        useShallow((state) => ({
            sorting: state.sorting,
            setSorting: state.setSorting,
            columnFilters: state.columnFilters,
            setColumnFilters: state.setColumnFilters,
            pagination: state.pagination,
            setPagination: state.setPagination,
        }))
    );

    // --- Initial State Synchronization Effect ---
    useEffect(() => {
        // Run only once on mount if syncWithUrl is enabled
        if (!syncWithUrl || isInitialized.current) {
            return;
        }
        isInitialized.current = true; // Mark as initialized

        // Create a mutable copy of searchParams for potential updates
        const newParams = new URLSearchParams(searchParams);
        let needsUrlUpdate = false; // Flag to track if URL needs updating

        // --- Sync Sorting ---
        const urlSortField = searchParams.get('sort');
        const urlSortDir = searchParams.get('dir') || 'asc'; // Default to 'asc' if dir is missing but sort exists
        let currentSorting = storeSorting; // Start with store state

        if (urlSortField) {
            // URL has sorting: Use it and update the store if different
            const urlSortingState: SortingState = [{ id: urlSortField, desc: urlSortDir === 'desc' }];
            // Basic comparison (can be improved for deep equality if needed)
            if (JSON.stringify(urlSortingState) !== JSON.stringify(storeSorting)) {
                setSorting(urlSortingState);
                currentSorting = urlSortingState; // Update currentSorting for subsequent checks/use
            }
            // Ensure URL reflects the potentially defaulted direction
            if (!searchParams.has('dir') && urlSortField) {
                newParams.set('dir', 'asc');
                needsUrlUpdate = true;
            }
        } else if (storeSorting.length > 0) {
            // No URL sorting, but store has sorting: Update URL from store
            newParams.set('sort', storeSorting[0].id);
            newParams.set('dir', storeSorting[0].desc ? 'desc' : 'asc');
            needsUrlUpdate = true;
        } else {
            // No sorting anywhere, ensure URL is clean
            if (searchParams.has('sort')) { newParams.delete('sort'); needsUrlUpdate = true; }
            if (searchParams.has('dir')) { newParams.delete('dir'); needsUrlUpdate = true; }
        }

        // --- Sync Filtering ---
        const urlFilters: ColumnFiltersState = [];
        let hasUrlFilters = false;
        for (const [key, value] of searchParams.entries()) {
            if (key.startsWith('filter_')) {
                hasUrlFilters = true;
                urlFilters.push({
                    id: key.replace('filter_', ''),
                    value: value,
                });
            }
        }
        let currentFilters = storeColumnFilters; // Start with store state

        if (hasUrlFilters) {
            // URL has filters: Use them and update the store if different
            // Basic comparison (can be improved for deep equality and order independence if needed)
            if (JSON.stringify(urlFilters.sort((a, b) => a.id.localeCompare(b.id))) !== JSON.stringify(storeColumnFilters.sort((a, b) => a.id.localeCompare(b.id)))) {
                setColumnFilters(urlFilters);
                currentFilters = urlFilters; // Update currentFilters
            }
        } else if (storeColumnFilters.length > 0) {
            // No URL filters, but store has filters: Update URL from store
            storeColumnFilters.forEach(filter => {
                newParams.set(`filter_${filter.id}`, String(filter.value));
            });
            needsUrlUpdate = true;
        } else {
            // No filters anywhere, ensure URL is clean
            Array.from(searchParams.keys())
                .filter(key => key.startsWith('filter_'))
                .forEach(key => {
                    newParams.delete(key);
                    needsUrlUpdate = true;
                });
        }

        // --- Sync Pagination ---
        const urlPage = searchParams.get('page');
        const urlSize = searchParams.get('size');
        let currentPagination = storePagination; // Start with store state

        const paginationUpdateFromUrl: Partial<PaginationState> = {};
        let urlHasPagination = false;

        if (urlPage !== null) {
            const pageIndex = Math.max(0, parseInt(urlPage, 10) || 0); // Default to 0 if invalid
            paginationUpdateFromUrl.pageIndex = pageIndex;
            urlHasPagination = true;
        }
        if (urlSize !== null) {
            const pageSize = Math.max(1, parseInt(urlSize, 10) || storePagination.pageSize); // Default to store's pageSize or 10 if invalid
            paginationUpdateFromUrl.pageSize = pageSize;
            urlHasPagination = true;
        }

        if (urlHasPagination) {
            // URL has pagination: Use it and update the store if different
            const newPaginationState = { ...storePagination, ...paginationUpdateFromUrl };
            if (newPaginationState.pageIndex !== storePagination.pageIndex || newPaginationState.pageSize !== storePagination.pageSize) {
                setPagination(newPaginationState);
                currentPagination = newPaginationState; // Update currentPagination
            }
            // Ensure URL reflects potentially defaulted/parsed values
            if (String(currentPagination.pageIndex) !== urlPage) {
                newParams.set('page', String(currentPagination.pageIndex));
                needsUrlUpdate = true;
            }
            if (String(currentPagination.pageSize) !== urlSize) {
                newParams.set('size', String(currentPagination.pageSize));
                needsUrlUpdate = true;
            }

        } else {
            // No URL pagination: Update URL from store (which has defaults or persisted values)
            newParams.set('page', String(storePagination.pageIndex));
            newParams.set('size', String(storePagination.pageSize));
            needsUrlUpdate = true;
        }

        // --- Apply URL Updates ---
        if (needsUrlUpdate) {
            setSearchParams(newParams, { replace: true }); // Use replace to avoid polluting history
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [syncWithUrl, searchParams, setSearchParams, /* Zustand setters are stable */ /* Store state values intentionally excluded to run only once */]);


    // --- Derive API Query Params from the *URL* ---
    // The URL is now the synchronized source of truth after the initial effect.
    // Subsequent updates will also modify the URL via other effects.
    const queryPage = parseInt(searchParams.get('page') || String(storePagination.pageIndex), 10); // Fallback to store default if somehow missing
    const queryPageSize = parseInt(searchParams.get('size') || String(storePagination.pageSize), 10); // Fallback to store default if somehow missing
    const querySortField = searchParams.get('sort') || undefined; // Use undefined if not present
    const querySortDirection = (searchParams.get('dir') || undefined) as 'asc' | 'desc' | undefined;
    const queryFilters: Record<string, string | undefined> = {};
    for (const [key, value] of searchParams.entries()) {
        if (key.startsWith('filter_')) {
            queryFilters[key.replace('filter_', '')] = value;
        }
    }

    // --- Data Fetching ---
    const {
        data: treatmentsResponse, // Contains { data: TData[], pageCount: number, totalCount: number } based on common patterns
        isLoading: isQueryLoading,
        isFetching: isQueryFetching,
        // Consider adding error handling
    } = trpc.treatments.getAll.useQuery(
        {
            page: queryPage,
            pageSize: queryPageSize,
            sortField: querySortField,
            sortDirection: querySortDirection,
            filters: queryFilters,
        },
        {
            suspense: true, // Keep suspense if you use it elsewhere
            // keepPreviousData: true, // Often useful for better UX during pagination/filtering
            // staleTime: 5 * 60 * 1000, // Example: Cache data for 5 minutes
        }
    );
    const isLoading = isQueryLoading; // Alias for clarity if needed below
    const isFetching = isQueryFetching; // Alias for clarity if needed below

    // --- State Synchronization Effects (State -> URL) ---
    // These run *after* the initial sync and handle user interactions

    // Sync sorting state with URL
    useEffect(() => {
        if (!syncWithUrl || !isInitialized.current) return; // Only run after init

        const currentSortField = searchParams.get('sort');
        const currentSortDir = searchParams.get('dir');
        const storeSortField = storeSorting[0]?.id;
        const storeSortDir = storeSorting[0]?.desc ? 'desc' : 'asc';

        // Check if URL needs update based on store state
        if (storeSortField !== currentSortField || (storeSortField && storeSortDir !== currentSortDir)) {
            const newParams = new URLSearchParams(searchParams);
            if (storeSorting.length > 0) {
                newParams.set('sort', storeSorting[0].id);
                newParams.set('dir', storeSorting[0].desc ? 'desc' : 'asc');
            } else {
                newParams.delete('sort');
                newParams.delete('dir');
            }
            setSearchParams(newParams, { replace: true });
        }
    }, [storeSorting, syncWithUrl, searchParams, setSearchParams]);

    // Sync filter state with URL
    useEffect(() => {
        if (!syncWithUrl || !isInitialized.current) return; // Only run after init

        const newParams = new URLSearchParams(searchParams);
        let filtersChanged = false;

        // Get current URL filters
        const urlFiltersMap = new Map<string, string>();
        Array.from(newParams.keys())
            .filter((key) => key.startsWith('filter_'))
            .forEach((key) => urlFiltersMap.set(key.replace('filter_', ''), newParams.get(key)!));

        // Get current Store filters
        const storeFiltersMap = new Map<string, string>();
        storeColumnFilters.forEach(f => storeFiltersMap.set(f.id, String(f.value)));

        // Check for differences and update URL
        // Remove filters from URL that are not in store
        urlFiltersMap.forEach((_, id) => {
            if (!storeFiltersMap.has(id)) {
                newParams.delete(`filter_${id}`);
                filtersChanged = true;
            }
        });

        // Add/Update filters in URL from store
        storeFiltersMap.forEach((value, id) => {
            if (urlFiltersMap.get(id) !== value) {
                newParams.set(`filter_${id}`, value);
                filtersChanged = true;
            }
        });


        if (filtersChanged) {
            setSearchParams(newParams, { replace: true });
        }
    }, [storeColumnFilters, syncWithUrl, searchParams, setSearchParams]);


    // Sync pagination state with URL
    useEffect(() => {
        if (!syncWithUrl || !isInitialized.current) return; // Only run after init

        const currentPage = searchParams.get('page');
        const currentSize = searchParams.get('size');
        const storePage = String(storePagination.pageIndex);
        const storeSize = String(storePagination.pageSize);

        if (currentPage !== storePage || currentSize !== storeSize) {
            const newParams = new URLSearchParams(searchParams);
            newParams.set('page', storePage);
            newParams.set('size', storeSize);
            setSearchParams(newParams, { replace: true });
        }
    }, [storePagination, syncWithUrl, searchParams, setSearchParams]);


    // --- React Table Instance ---
    // It now reads directly from the Zustand store, which is kept in sync with the URL
    const [rowSelection, setRowSelection] = useState({}); // Local UI state

    const table = useReactTable({
        // Provide default empty array while loading, or use keepPreviousData in useQuery
        data: treatmentsResponse?.data ?? [],
        columns,
        pageCount: treatmentsResponse?.pageCount ?? -1, // Use -1 for unknown page count if needed
        state: {
            // Feed table state from Zustand store
            sorting: storeSorting,
            columnFilters: storeColumnFilters,
            pagination: storePagination,
            rowSelection,
        },
        // Manual handlers delegate state updates to Zustand setters
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        // Local UI state handled directly
        onRowSelectionChange: setRowSelection,
        // Inform table that pagination/sorting/filtering is done server-side
        manualPagination,
        manualSorting,
        manualFiltering,
        // Get model functions are still needed by the table internally
        getCoreRowModel: getCoreRowModel(),
        // Conditionally include client-side models if manual flags are false
        getPaginationRowModel: manualPagination ? undefined : getPaginationRowModel(),
        getSortedRowModel: manualSorting ? undefined : getSortedRowModel(),
        getFilteredRowModel: manualFiltering ? undefined : getFilteredRowModel(),
        // debugTable: true, // Uncomment for debugging
    });

    // --- Clamp Page Index Effect (Optional but Recommended) ---
    // This runs *after* data fetching and adjusts the page if it's out of bounds
    useEffect(() => {
        if (!treatmentsResponse || isLoading || isFetching || !manualPagination) {
            return;
        }

        const currentPageIndex = table.getState().pagination.pageIndex;
        const pageCount = treatmentsResponse.pageCount; // Assuming API returns pageCount

        if (pageCount > 0 && currentPageIndex >= pageCount) {
            // Go to the last valid page
            table.setPageIndex(Math.max(0, pageCount - 1));
        }
        // Optional: If on page 0 but pageCount is 0 (no results), maybe reset filters?
        // else if (pageCount === 0 && currentPageIndex !== 0) {
        //    table.setPageIndex(0);
        // }

    }, [treatmentsResponse, table, isLoading, isFetching, manualPagination]);


    // --- Render Logic ---

    // Loading Skeleton
    if (isLoading) { // Show skeleton only on initial load
        return (
            <div className='bg-white rounded-lg shadow-sm p-6'>
                <div className='flex items-center justify-between py-4'>
                    <Skeleton className='h-10 w-[250px]' />
                    <Skeleton className='h-10 w-[150px]' />
                </div>
                <div className='rounded-md border border-gray-200'>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                {/* Simple skeleton based on column count */}
                                {Array.from({ length: columns.length }).map((_, index) => (
                                    <TableHead key={index}>
                                        <Skeleton className='h-6 w-full' />
                                    </TableHead>
                                ))}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {Array.from({ length: table.getState().pagination.pageSize }).map((_, rowIndex) => (
                                <TableRow key={rowIndex}>
                                    {Array.from({ length: columns.length }).map((_, colIndex) => (
                                        <TableCell key={colIndex}>
                                            <Skeleton className='h-6 w-full' />
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
                <div className='flex items-center justify-between space-x-2 py-4 mt-2'>
                    <Skeleton className='h-6 w-24' />
                    <div className="flex space-x-2">
                        <Skeleton className='h-9 w-20' />
                        <Skeleton className='h-9 w-20' />
                    </div>
                </div>
            </div>
        );
    }

    // Main Table Render
    return (
        <div className='bg-white rounded-lg shadow-sm p-6'>
            {/* Filter Input & Add Button */}
            <div className='flex items-center justify-between py-4'>
                <div className='flex gap-4 items-center'>
                    {/* Example: Specific filter tied to the 'name' column */}
                    <Input
                        placeholder='Filter by name...'
                        // Ensure type safety accessing filter value
                        value={(table.getColumn('name')?.getFilterValue() as string) ?? ''}
                        onChange={(event) => {
                            // This directly calls the onColumnFiltersChange -> setColumnFilters (Zustand)
                            table.getColumn('name')?.setFilterValue(event.target.value)
                        }}
                        className='max-w-sm focus:ring-primary'
                    />
                    {/* Display loading indicator during fetches (not initial load) */}
                    {isFetching && (
                        <div className='flex items-center text-sm text-muted-foreground'>
                            <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                            Loading...
                        </div>
                    )}
                </div>
                {/* Example Add Button */}
                <CreateTreatmentButton />
            </div>

            {/* Table */}
            <div className='rounded-md border border-gray-200 overflow-hidden'>
                <Table>
                    <TableHeader className='bg-gray-50'>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => {
                                    return (
                                        <TableHead key={header.id} className='font-medium text-gray-700'>
                                            {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                                        </TableHead>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {table.getRowModel().rows?.length ? (
                            table.getRowModel().rows.map((row) => (
                                <TableRow
                                    key={row.id}
                                    data-state={row.getIsSelected() && 'selected'}
                                    className='cursor-pointer hover:bg-gray-50 transition-colors relative'
                                >
                                    {row.getVisibleCells().map((cell, index) => (
                                        <TableCell key={cell.id} style={{ width: cell.column.getSize() !== 150 ? cell.column.getSize() : undefined }}>
                                            {index === 0 && (
                                                <Link
                                                    to={`/treatment/${(row.original as any).id}`}
                                                    className="absolute inset-0 z-[1]"
                                                    tabIndex={-1}
                                                />
                                            )}
                                            {cell.column.id === 'actions' ? (
                                                <div className="relative z-[2]">
                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                </div>
                                            ) : (
                                                flexRender(cell.column.columnDef.cell, cell.getContext())
                                            )}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={columns.length} className='h-24 text-center'>
                                    {isFetching ? (
                                        <div className='flex justify-center items-center'>
                                            <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                                            Loading data...
                                        </div>
                                    ) : (
                                        // Provide a more informative message based on filters
                                        queryFilters && Object.keys(queryFilters).length > 0
                                            ? 'No treatments found matching your filters.'
                                            : 'No treatments found.'
                                    )}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>

            {/* Pagination Controls */}
            <div className='flex items-center justify-between space-x-2 py-4 mt-2'>
                {/* Optional: Show selected row count */}
                {/* <div className="flex-1 text-sm text-muted-foreground">
                    {table.getFilteredSelectedRowModel().rows.length} of{" "}
                    {table.getFilteredRowModel().rows.length} row(s) selected.
                </div> */}
                <div className='flex items-center space-x-2'>
                    {/* Optional: Rows per page selector */}
                    <select
                        value={table.getState().pagination.pageSize}
                        onChange={e => {
                            table.setPageSize(Number(e.target.value))
                        }}
                        className="border border-gray-300 rounded px-2 py-1 text-sm"
                        disabled={isFetching}
                    >
                        {[10, 20, 30, 40, 50].map(pageSize => (
                            <option key={pageSize} value={pageSize}>
                                Show {pageSize}
                            </option>
                        ))}
                    </select>

                    <div className='text-sm text-muted-foreground'>
                        Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount() > 0 ? table.getPageCount() : 1} {/* Handle pageCount=0 */}
                    </div>
                    <Button
                        variant='outline'
                        size='sm'
                        onClick={() => table.previousPage()}
                        disabled={!table.getCanPreviousPage() || isFetching}
                        className='border-gray-300 hover:bg-gray-50'
                    >
                        Previous
                    </Button>
                    <Button
                        variant='outline'
                        size='sm'
                        onClick={() => table.nextPage()}
                        disabled={!table.getCanNextPage() || isFetching}
                        className='border-gray-300 hover:bg-gray-50'
                    >
                        Next
                    </Button>
                </div>
            </div>
        </div>
    );
};

// Simplified Treatments component that just renders the TreatmentsDataTable
const Treatments = () => {
    return (
        <Content>
            <Tabs defaultValue="search">
                <TabsList className="grid grid-cols-2">
                    <TabsTrigger value="search">Search Treatments</TabsTrigger>
                    <TabsTrigger value="order">Order Treatments</TabsTrigger>
                </TabsList>
                <TabsContent value="search">
                    <div className='container mx-auto py-10'>
                        <h1 className='text-2xl font-bold mb-6'>Treatments</h1>
                        <DataTable columns={columns} manualPagination={true} manualSorting={true} manualFiltering={true} syncWithUrl={true} />
                    </div>
                </TabsContent>
                <TabsContent value="order">
                    <TreatmentsOrdering />
                </TabsContent>
            </Tabs>
        </Content>
    );
};

export default Treatments;
